/**
 * The bottom sheet behind the tab bar's More.
 *
 * It started with one way out — tap the dimmed area — and that turned out to be no ways out on an
 * iPhone: "the only way I can dismiss this slide up menu is by picking an item on it." A sheet
 * that can only be left by choosing something is a sheet that forces a choice, which is not what a
 * navigation menu is for.
 *
 * So it now closes four ways, and they are independent on purpose. Whatever iOS is doing to the
 * scrim's `click` — a synthesised click can be lost to a scroll that never happened, to the
 * double-tap wait, to an element inserted under a finger mid-gesture — `pointerup` fires anyway,
 * and a swipe and a key do not go through the scrim at all.
 *
 *   TAP THE DIMMED AREA. On `pointerup`, not `click`. The click that iOS synthesises afterwards is
 *   swallowed once, or it would land on whatever the scrim was covering and press it.
 *   SWIPE THE SHEET DOWN. The grip at the top says the sheet can be dragged; it now can.
 *   PRESS ESCAPE. For an iPad with a keyboard, and for the desktop layout where this never shows
 *   but the habit is universal.
 *   TAP MORE AGAIN. The tab bar sits above the scrim so the button that opened it can close it.
 */

import { useEffect, type ReactNode } from 'react';
import { useSheetSwipe } from './useSheetSwipe.js';
import { useScrollLock } from './useScrollLock.js';

export interface MoreItem {
  id: string;
  label: string;
  detail: string;
}

export function MoreSheet(
  { items, onPick, onClose, children }:
  {
    items: readonly MoreItem[];
    onPick: (id: string) => void;
    onClose: () => void;
    /** Rendered under the sections — the identity block, on a phone. */
    children?: ReactNode;
  },
) {
  const swipe = useSheetSwipe(onClose);

  useScrollLock(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  /*
   * Closing on `pointerup` means the `click` iOS makes from the same tap arrives after the scrim
   * has gone, at a point that is now over the page. Swallow exactly one, in the capture phase, so
   * dismissing a menu never also presses whatever the menu was covering.
   */
  const closeSwallowingClick = () => {
    const swallow = (e: Event) => { e.stopPropagation(); e.preventDefault(); };
    window.addEventListener('click', swallow, { capture: true, once: true });
    // If no click follows — a mouse that was dragged, a pointer that was cancelled — the listener
    // would sit there and eat the person's NEXT click instead. One frame is long enough.
    window.setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 400);
    onClose();
  };

  return (
    <>
      <button type="button" className="sheetscrim" aria-label="Close"
        onPointerUp={closeSwallowingClick} />
      <div className="moresheet" role="dialog" aria-label="More sections"
        style={swipe.style} {...swipe.handlers}>
        <span className="sheetgrip" aria-hidden="true" />
        {items.map((s) => (
          <button key={s.id} type="button" className="sheetrow" onClick={() => onPick(s.id)}>
            <b>{s.label}</b>
            <span className="mut">{s.detail}</span>
          </button>
        ))}
        {children}
      </div>
    </>
  );
}
